import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useManagerPayrollAccess } from '../../hooks/useManagerPayrollAccess';
import {
  IcoPlus, IcoUsers, IcoCalendar, IcoDollar,
  IcoSettings, IcoLock, IcoZap,
} from '../dashboard/DashIcons';

const SIDEBAR_W = 220;

export default function QuickActionsSidebar() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { features } = useOrgSettings();
  const { hasAccess: hasMgrPayrollAccess } = useManagerPayrollAccess();

  if (!user) return null;

  const isAdminAssistant = user.role === 'adminassistant';
  const isManager        = user.role === 'manager';
  const isAdminUser      = user.role === 'admin';

  const toggle = () => setOpen(o => !o);

  const go = path => { navigate(path); };

  const actions = [
    { label: 'Add Employee',   Icon: IcoPlus,     path: '/employee/new',   show: isSuperAdmin() || isAdminAssistant || isManager },
    { label: 'Team Directory', Icon: IcoUsers,    path: '/team',           show: true },
    { label: 'Schedule',       Icon: IcoCalendar, path: '/schedule',       show: !!features.scheduling },
    { label: 'Run Payroll',    Icon: IcoDollar,   path: '/payroll',        show: !!features.payroll && !isAdminAssistant },
    { label: 'Manager Pay',    Icon: IcoLock,     path: '/manager-payroll',show: !!hasMgrPayrollAccess },
    { label: 'Settings',       Icon: IcoSettings, path: '/settings',       show: isSuperAdmin() || isAdminUser },
  ].filter(a => a.show);

  return (
    <>
      {/* Slide-in panel */}
      <div style={{
        position: 'fixed',
        top: 70,
        right: open ? 0 : -SIDEBAR_W,
        width: SIDEBAR_W,
        height: 'calc(100vh - 70px)',
        background: 'var(--ce-card-bg)',
        borderLeft: '1px solid var(--ce-card-border)',
        boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
        zIndex: 150,
        transition: 'right 0.25s ease, box-shadow 0.25s ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1rem',
          borderBottom: '1px solid var(--ce-card-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: '700', fontSize: '0.9rem', color: 'var(--ce-dash-text)' }}>
            <IcoZap size={15} color="#22c55e" /> Quick Actions
          </span>
          <button onClick={toggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ce-dash-text-soft)', fontSize: '1rem', lineHeight: 1, padding: '2px 4px' }}>✕</button>
        </div>

        {/* Action buttons */}
        <div style={{ padding: '0.625rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', overflowY: 'auto' }}>
          {actions.map(({ label, Icon, path }) => (
            <button
              key={path}
              onClick={() => go(path)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.65rem 0.875rem',
                background: 'var(--ce-quick-link-bg)',
                border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '0.875rem',
                fontWeight: '600', color: 'var(--ce-quick-link-text)',
                textAlign: 'left', width: '100%',
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#22c55e'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--ce-quick-link-bg)'; e.currentTarget.style.color = 'var(--ce-quick-link-text)'; }}
            >
              <Icon size={14} color="currentColor" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Toggle tab — always visible on the right edge */}
      <button
        onClick={toggle}
        title={open ? 'Close Quick Actions' : 'Open Quick Actions'}
        style={{
          position: 'fixed',
          right: open ? SIDEBAR_W : 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 151,
          background: '#15803d',
          color: 'white',
          border: 'none',
          borderRadius: '8px 0 0 8px',
          padding: '12px 7px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          transition: 'right 0.25s ease',
          userSelect: 'none',
        }}
      >
        {open ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <span style={{ writingMode: 'vertical-rl', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1 }}>Actions</span>
          </>
        )}
      </button>
    </>
  );
}
